import { Button as ChakraButton, Flex, Text } from '@chakra-ui/react';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import { useId } from 'react';

function SyncUpdateIcon() {
  const maskId = useId();
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id={maskId}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="20"
        height="20"
      >
        <rect width="20" height="20" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path
          d="M10 17.5C8.95833 17.5 7.98264 17.3021 7.07292 16.9062C6.16319 16.5104 5.37153 15.9757 4.69792 15.3021C4.02431 14.6285 3.48958 13.8368 3.09375 12.9271C2.69792 12.0174 2.5 11.0417 2.5 10C2.5 8.95833 2.69792 7.98264 3.09375 7.07292C3.48958 6.16319 4.02431 5.37153 4.69792 4.69792C5.37153 4.02431 6.16319 3.48958 7.07292 3.09375C7.98264 2.69792 8.95833 2.5 10 2.5C11.1389 2.5 12.2188 2.74306 13.2396 3.22917C14.2604 3.71528 15.125 4.40278 15.8333 5.29167V4.16667C15.8333 3.93056 15.9132 3.73264 16.0729 3.57292C16.2326 3.41319 16.4306 3.33333 16.6667 3.33333C16.9028 3.33333 17.1007 3.41319 17.2604 3.57292C17.4201 3.73264 17.5 3.93056 17.5 4.16667V7.5C17.5 7.73611 17.4201 7.93403 17.2604 8.09375C17.1007 8.25347 16.9028 8.33333 16.6667 8.33333H13.3333C13.0972 8.33333 12.8993 8.25347 12.7396 8.09375C12.5799 7.93403 12.5 7.73611 12.5 7.5C12.5 7.26389 12.5799 7.06597 12.7396 6.90625C12.8993 6.74653 13.0972 6.66667 13.3333 6.66667H14.7917C14.2222 5.88889 13.5208 5.27778 12.6875 4.83333C11.8542 4.38889 10.9583 4.16667 10 4.16667C8.375 4.16667 6.99653 4.73264 5.86458 5.86458C4.73264 6.99653 4.16667 8.375 4.16667 10C4.16667 11.625 4.73264 13.0035 5.86458 14.1354C6.99653 15.2674 8.375 15.8333 10 15.8333C11.3194 15.8333 12.5 15.4375 13.5417 14.6458C14.5833 13.8542 15.2708 12.8333 15.6042 11.5833C15.6736 11.3611 15.7986 11.1944 15.9792 11.0833C16.1597 10.9722 16.3611 10.9306 16.5833 10.9583C16.8194 10.9861 17.0069 11.0868 17.1458 11.2604C17.2847 11.434 17.3264 11.625 17.2708 11.8333C16.8681 13.4861 15.9931 14.8437 14.6458 15.9062C13.2986 16.9687 11.75 17.5 10 17.5ZM10.8333 9.66667L12.9167 11.75C13.0694 11.9028 13.1458 12.0972 13.1458 12.3333C13.1458 12.5694 13.0694 12.7639 12.9167 12.9167C12.7639 13.0694 12.5694 13.1458 12.3333 13.1458C12.0972 13.1458 11.9028 13.0694 11.75 12.9167L9.41667 10.5833C9.33333 10.5 9.27083 10.4062 9.22917 10.3021C9.1875 10.1979 9.16667 10.0903 9.16667 9.97917V6.66667C9.16667 6.43056 9.24653 6.23264 9.40625 6.07292C9.56597 5.91319 9.76389 5.83333 10 5.83333C10.2361 5.83333 10.434 5.91319 10.5938 6.07292C10.7535 6.23264 10.8333 6.43056 10.8333 6.66667V9.66667Z"
          fill="#8E8E93"
        />
      </g>
    </svg>
  );
}

interface SyncInfoProps {
  lastSyncedAt?: number | null;
  sourceLhcNo?: string;
  onSync?: () => void;
}

export function SyncInfo({ lastSyncedAt, sourceLhcNo, onSync }: SyncInfoProps) {
  const formattedDate =
    typeof lastSyncedAt === 'number'
      ? format(new Date(lastSyncedAt * 1000), "EEEE, d MMM yyyy '·' HH:mm", {
          locale: localeId,
        })
      : '-';

  const infoLabel = sourceLhcNo ? 'Sumber file' : 'Sinkronisasi Terakhir';
  const infoValue = sourceLhcNo ? `${sourceLhcNo} SOL` : formattedDate;

  return (
    <Flex
      align="center"
      gap={2}
      width="100%"
      justify={onSync ? 'space-between' : 'flex-start'}
    >
      <Flex align="center" gap={2}>
        <SyncUpdateIcon />
        <Text fontSize="14px" fontWeight={500} color="gray.500">
          {infoLabel}
        </Text>
        <Text fontSize="14px" fontWeight={500} color="gray.700">
          {infoValue}
        </Text>
      </Flex>
      {onSync && (
        <ChakraButton
          size="sm"
          bg="gray.100"
          leftIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
          color="blue.700"
          fontWeight="normal"
          _hover={{ bg: 'gray.200' }}
          onClick={onSync}
        >
          Sync
        </ChakraButton>
      )}
    </Flex>
  );
}
